"use client"

import type { CSSProperties, ReactNode } from "react"

export type BadgeTone =
  | "neutral"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "brand"

type Variant = "soft" | "solid" | "outline"
type Size = "sm" | "md"

const TONE_SOFT: Record<
  BadgeTone,
  { bg: string; text: string; border: string }
> = {
  neutral: {
    bg: "var(--redy-warm-white)",
    text: "var(--redy-secondary)",
    border: "var(--redy-border)",
  },
  success: {
    bg: "var(--redy-success-bg)",
    text: "var(--redy-success)",
    border: "rgba(45,122,58,0.28)",
  },
  warning: {
    bg: "#FFF8E1",
    text: "#9C6800",
    border: "rgba(244,168,31,0.32)",
  },
  error: {
    bg: "var(--redy-error-bg)",
    text: "var(--redy-error)",
    border: "rgba(215,38,61,0.28)",
  },
  info: {
    bg: "#F0F4FF",
    text: "#2850A0",
    border: "rgba(74,122,232,0.28)",
  },
  brand: {
    bg: "#FFF0F1",
    text: "var(--redy-red)",
    border: "rgba(215,38,61,0.30)",
  },
}

const TONE_SOLID: Record<BadgeTone, { bg: string; text: string }> = {
  neutral: { bg: "var(--redy-charcoal)", text: "#FFFFFF" },
  success: { bg: "var(--redy-success)", text: "#FFFFFF" },
  warning: { bg: "#F4A81F", text: "#1A1A1A" },
  error: { bg: "var(--redy-error)", text: "#FFFFFF" },
  info: { bg: "#4A7AE8", text: "#FFFFFF" },
  brand: { bg: "var(--redy-red)", text: "#FFFFFF" },
}

const SIZE_CLS: Record<Size, string> = {
  sm: "text-[11px] font-semibold px-2 py-0.5",
  md: "text-[11.5px] font-bold px-2.5 py-[3px]",
}

const DOT_GLOW_RGBA: Record<BadgeTone, string> = {
  neutral: "rgba(107,103,98,0.14)",
  success: "rgba(46,204,113,0.16)",
  warning: "rgba(244,168,31,0.16)",
  error: "rgba(215,38,61,0.14)",
  info: "rgba(74,122,232,0.14)",
  brand: "rgba(215,38,61,0.16)",
}

export type BadgeProps = {
  tone?: BadgeTone
  variant?: Variant
  size?: Size
  dot?: boolean
  glow?: boolean
  children: ReactNode
  className?: string
}

export function Badge({
  tone = "neutral",
  variant = "soft",
  size = "sm",
  dot = false,
  glow = false,
  children,
  className = "",
}: BadgeProps) {
  let style: CSSProperties = {}
  let extraCls = ""

  if (variant === "soft") {
    const t = TONE_SOFT[tone]
    style = { background: t.bg, color: t.text, borderColor: t.border }
    extraCls = "border"
  } else if (variant === "solid") {
    const t = TONE_SOLID[tone]
    style = { background: t.bg, color: t.text }
  } else if (variant === "outline") {
    const t = TONE_SOFT[tone]
    style = { color: t.text, borderColor: t.border, background: "transparent" }
    extraCls = "border"
  }

  const dotBg =
    variant === "solid"
      ? "currentColor"
      : tone === "success" && glow
        ? "var(--redy-success-bright)"
        : TONE_SOFT[tone].text

  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full whitespace-nowrap",
        SIZE_CLS[size],
        extraCls,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
    >
      {dot && (
        <span
          className="rounded-full shrink-0"
          style={{
            width: 6,
            height: 6,
            background: dotBg,
            boxShadow: glow ? `0 0 0 3px ${DOT_GLOW_RGBA[tone]}` : undefined,
          }}
          aria-hidden
        />
      )}
      {children}
    </span>
  )
}

// ── StatusBadge — domain status → tone + label mapping ──────────────────────
//
// Single source of truth for order/employee/terminal/inventory statuses
// across the admin portal. Pass a `status` string from the DB and it picks
// the canonical tone + Spanish label.

const STATUS_TONE: Record<string, { tone: BadgeTone; label: string }> = {
  // Order lifecycle
  confirmed: { tone: "warning", label: "Confirmada" },
  pending: { tone: "warning", label: "Pendiente" },
  processing: { tone: "warning", label: "Procesando" },
  in_progress: { tone: "brand", label: "En proceso" },
  preparing: { tone: "brand", label: "En proceso" },
  ready: { tone: "success", label: "Lista" },
  completed: { tone: "neutral", label: "Completada" },
  delivered: { tone: "neutral", label: "Entregada" },
  voided: { tone: "neutral", label: "Anulada" },
  cancelled: { tone: "neutral", label: "Cancelada" },

  // Payment
  paid: { tone: "success", label: "Pagada" },
  unpaid: { tone: "warning", label: "Sin pagar" },
  partially_paid: { tone: "warning", label: "Pago parcial" },
  refunded: { tone: "info", label: "Reembolsada" },
  partial_refund: { tone: "info", label: "Reembolso parcial" },
  partially_refunded: { tone: "info", label: "Reembolso parcial" },
  failed: { tone: "error", label: "Fallida" },
  declined: { tone: "error", label: "Rechazada" },

  // Entity (employee, terminal, item, etc.)
  active: { tone: "success", label: "Activo" },
  inactive: { tone: "neutral", label: "Inactivo" },
  disabled: { tone: "neutral", label: "Desactivado" },
  archived: { tone: "neutral", label: "Archivado" },
  draft: { tone: "neutral", label: "Borrador" },
  published: { tone: "success", label: "Publicado" },
  available: { tone: "success", label: "Disponible" },
  unavailable: { tone: "warning", label: "Agotado" },
  out_of_stock: { tone: "error", label: "Sin stock" },
  low_stock: { tone: "warning", label: "Stock bajo" },

  // Generic
  live: { tone: "brand", label: "En vivo" },
  scheduled: { tone: "info", label: "Programado" },
  error: { tone: "error", label: "Error" },
  warning: { tone: "warning", label: "Aviso" },
  new: { tone: "info", label: "Nuevo" },
}

/**
 * Las mismas etiquetas en inglés. Vive aquí (y no en next-intl) porque
 * StatusBadge también se pinta fuera del panel —`display/[slug]` (KDS),
 * superadmin, /design-system— donde puede no haber NextIntlClientProvider:
 * un `useTranslations` adentro tumbaría esas pantallas. El panel pasa
 * `locale={useLocale()}`; quien no lo pasa ve exactamente lo de siempre.
 */
const STATUS_LABEL_EN: Record<string, string> = {
  confirmed: "Confirmed",
  pending: "Pending",
  processing: "Processing",
  in_progress: "In progress",
  preparing: "In progress",
  ready: "Ready",
  completed: "Completed",
  delivered: "Delivered",
  voided: "Voided",
  cancelled: "Cancelled",

  paid: "Paid",
  unpaid: "Unpaid",
  partially_paid: "Partially paid",
  refunded: "Refunded",
  partial_refund: "Partial refund",
  partially_refunded: "Partial refund",
  failed: "Failed",
  declined: "Declined",

  active: "Active",
  inactive: "Inactive",
  disabled: "Disabled",
  archived: "Archived",
  draft: "Draft",
  published: "Published",
  available: "Available",
  unavailable: "Sold out",
  out_of_stock: "Out of stock",
  low_stock: "Low stock",

  live: "Live",
  scheduled: "Scheduled",
  error: "Error",
  warning: "Warning",
  new: "New",
}

/** Ausente o distinto de "en" ⇒ español (lo de siempre). */
export type StatusBadgeLocale = "es" | "en" | (string & {})

function localizedStatusLabel(status: string, locale: StatusBadgeLocale | undefined): string | undefined {
  if (locale === "en") return STATUS_LABEL_EN[status]
  return STATUS_TONE[status]?.label
}

export type StatusBadgeProps = {
  status: string | null | undefined
  label?: string
  /** Idioma de la etiqueta canónica. Default "es". Solo afecta a estados
   *  conocidos; un `label` explícito o un estado desconocido se pintan tal cual. */
  locale?: StatusBadgeLocale
  size?: Size
  dot?: boolean
  glow?: boolean
  className?: string
}

export function StatusBadge({
  status,
  label,
  locale,
  size = "sm",
  dot = false,
  glow = false,
  className,
}: StatusBadgeProps) {
  const known = status ? STATUS_TONE[status] : undefined
  const meta = known
    ? { tone: known.tone, label: localizedStatusLabel(status as string, locale) ?? known.label }
    : { tone: "neutral" as BadgeTone, label: status ?? "—" }
  return (
    <Badge
      tone={meta.tone}
      variant="soft"
      size={size}
      dot={dot}
      glow={glow}
      className={className}
    >
      {label ?? meta.label}
    </Badge>
  )
}

/** Use when you need to know the tone for non-Badge UI (e.g. row tint). */
export function statusTone(status: string | null | undefined): BadgeTone {
  return (status && STATUS_TONE[status]?.tone) || "neutral"
}

/** La etiqueta que pinta <StatusBadge>, para cuando el estado sale de la UI —un
 *  CSV, un correo— y tiene que leerse igual que en pantalla ("Pagada", no "paid").
 *  Mismo fallback que el badge: el valor crudo, o "—" si no hay. */
export function statusLabel(status: string | null | undefined, locale?: StatusBadgeLocale): string {
  return (status && localizedStatusLabel(status, locale)) || status || "—"
}
